import { useState, useRef, useEffect } from "react";
import { sendGstOtpApi, verifyGstOtpApi } from "../../api/verification";
import { VerificationStatus } from "./VerificationStatus";
import { OrganisationAlreadyRegisteredCard } from "./OrganisationAlreadyRegisteredCard";
import { C, font } from "../../styles/tokens";
import { InlineIcon } from "../InlineIcon";

export function GstVerification({
  accountType,
  gstin,
  onGstinChange,
  onVerificationSuccess,
  defaultEmail = "",
  onSwitchToPan,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // OTP State
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [refId, setRefId] = useState(null);
  const [maskedMobile, setMaskedMobile] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [timer, setTimer] = useState(600); // 10 minutes
  const [canResend, setCanResend] = useState(false);

  // Result States
  const [verifiedData, setVerifiedData] = useState(null);
  const [alreadyRegisteredData, setAlreadyRegisteredData] = useState(null);

  const isSubmittingRef = useRef(false);

  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  const cleanGst = (gstin || "").trim().toUpperCase();
  const isValidFormat = gstRegex.test(cleanGst);

  const isAuth4037 =
    typeof error === "string" &&
    (error.includes("AUTH4037") ||
      error.toLowerCase().includes("api access is not available") ||
      error.toLowerCase().includes("manage api access") ||
      error.toLowerCase().includes("user expiry duration"));

  // Countdown timer for OTP
  useEffect(() => {
    let interval = null;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          if (prev === 570) {
            setCanResend(true); // Allow resend after 30 seconds
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  const formatTimer = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // Step 1: Send OTP to GST Signatory
  const handleSendOtp = async () => {
    if (!isValidFormat || loading || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setError(null);
    setAlreadyRegisteredData(null);
    setLoading(true);

    try {
      const normalizedAccountType =
        accountType === "ORGANISATION" || accountType === "ORGANISATION_OFFICER"
          ? "ORGANISATION"
          : "ACADEMIC_INSTITUTION";

      const res = await sendGstOtpApi({
        gstin: cleanGst,
        accountType: normalizedAccountType,
      });

      if (res?.alreadyRegistered) {
        setAlreadyRegisteredData(res);
        return;
      }

      if (res?.success) {
        setOtpSent(true);
        setRefId(res.refId || res.txn);
        setMaskedMobile(res.maskedMobile || "******9821");
        setMaskedEmail(res.maskedEmail || "s****@enterprise.com");
        setTimer(600);
        setCanResend(false);
        setOtp("");
      } else {
        throw new Error(
          res?.message ||
            "Failed to send GST OTP. Please check your GSTIN and try again.",
        );
      }
    } catch (err) {
      setError(
        err?.message ||
          "Failed to send GST OTP. Please check your GSTIN and try again.",
      );
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async () => {
    if (
      !otp ||
      otp.trim().length !== 6 ||
      !refId ||
      loading ||
      isSubmittingRef.current
    ) {
      return;
    }

    isSubmittingRef.current = true;
    setError(null);
    setLoading(true);

    try {
      const normalizedAccountType =
        accountType === "ORGANISATION" || accountType === "ORGANISATION_OFFICER"
          ? "ORGANISATION"
          : "ACADEMIC_INSTITUTION";

      const res = await verifyGstOtpApi({
        gstin: cleanGst,
        refId,
        otp: otp.trim(),
        accountType: normalizedAccountType,
      });

      if (res?.alreadyRegistered) {
        setAlreadyRegisteredData(res);
        return;
      }

      if (res?.success && res?.verificationToken) {
        setVerifiedData(res);
        setOtpSent(false);

        if (onVerificationSuccess) {
          onVerificationSuccess({
            verificationToken: res.verificationToken,
            legalName: res.legalName,
            tradeName: res.tradeName,
            gstin: res.gstin || cleanGst,
            address: res.address,
            addressLine1: res.addressLine1,
            city: res.city,
            state: res.state,
            pincode: res.pincode,
            primarySignatoryName: res.primarySignatoryName,
            status: res.status || "ACTIVE",
            signatories: res.signatories || [],
            mobile: res.mobile || undefined,
            phone: res.phone || undefined,
            verificationType: "GST",
          });
        }
      } else {
        throw new Error(
          res?.message || "Invalid or expired GST OTP. Please try again.",
        );
      }
    } catch (err) {
      setError(
        err?.message ||
          "Invalid or expired GST OTP. Please check the code and try again.",
      );
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const handleReset = () => {
    setVerifiedData(null);
    setOtpSent(false);
    setOtp("");
    setRefId(null);
    setError(null);
    setAlreadyRegisteredData(null);
    if (onGstinChange) onGstinChange("");
  };

  return (
    <div style={boxStyle}>
      <div style={headerStyle}>
        <div className="flex items-center gap-2">
          <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>
            GSTIN Identity Verification
          </span>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            GSTN Verified
          </span>
        </div>
        {verifiedData && (
          <span style={badgeStyle}>
            <InlineIcon name="check" size={10} /> VERIFIED
          </span>
        )}
      </div>

      {/* Step 1: GSTIN Input & Send OTP Button */}
      {!otpSent && !verifiedData && (
        <div style={{ marginTop: 10 }}>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <input
              type="text"
              placeholder="e.g. 27AABCU9603R1ZN"
              value={gstin || ""}
              onChange={(e) => {
                onGstinChange(e.target.value.toUpperCase());
                if (error) setError(null);
                if (alreadyRegisteredData) setAlreadyRegisteredData(null);
              }}
              disabled={loading}
              maxLength={15}
              className="w-full sm:flex-1 px-3 py-2 text-base sm:text-[13px] border border-slate-300 rounded-md tracking-wider outline-none focus:border-blue-500"
              style={{ fontFamily: font }}
            />

            <button
              type="button"
              onClick={handleSendOtp}
              disabled={!isValidFormat || loading}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 text-sm sm:text-[13px] font-semibold text-white rounded-md transition duration-150 flex items-center justify-center shrink-0"
              style={{
                background:
                  isValidFormat && !loading
                    ? C.blue || "#0284c7"
                    : C.textLight || "#94a3b8",
                cursor: isValidFormat && !loading ? "pointer" : "default",
                fontFamily: font,
              }}
            >
              {loading ? "Sending OTP..." : "Send GST OTP"}
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5 mb-0">
            An official 6-digit OTP will be dispatched via GSTN to the Primary
            Authorized Signatory on record.
          </p>

          {/* PAN Checkbox below the GST input box */}
          {onSwitchToPan && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
              <label
                htmlFor="panToggleInGst"
                className="flex items-center gap-2 select-none text-xs cursor-pointer text-slate-700 hover:text-slate-900 font-medium"
              >
                <input
                  type="checkbox"
                  id="panToggleInGst"
                  checked={false}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSwitchToPan();
                    }
                  }}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="font-semibold text-slate-800">
                  Verify using PAN Card instead of GSTIN
                </span>
              </label>

              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200">
                GST OTP Mode
              </span>
            </div>
          )}
        </div>
      )}

      {/* Step 2: OTP Entry Form */}
      {otpSent && !verifiedData && (
        <div
          style={{ marginTop: 12 }}
          className="p-3.5 bg-slate-50 rounded-lg border border-slate-200"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-slate-800">
              Enter 6-Digit GST OTP
            </div>
            <button
              type="button"
              onClick={() => setOtpSent(false)}
              className="text-xs text-blue-600 hover:underline font-medium cursor-pointer"
            >
              Edit GSTIN
            </button>
          </div>

          <p
            className="text-[11px] text-slate-600 mb-3"
            style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
          >
            OTP sent to signatory contact:{" "}
            <strong className="text-slate-800">{maskedMobile}</strong> &amp;{" "}
            <strong className="text-slate-800">{maskedEmail}</strong>
          </p>

          <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-2 items-stretch sm:items-center">
            <input
              type="text"
              placeholder="6-digit OTP"
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              maxLength={6}
              disabled={loading}
              className="w-full sm:w-40 px-3 py-2 text-center text-base sm:text-sm font-bold tracking-widest rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
            />

            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={otp.length !== 6 || loading}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-4 py-2 text-sm sm:text-[13px] font-semibold text-white rounded-md transition duration-150 flex items-center justify-center shrink-0"
              style={{
                background:
                  otp.length === 6 && !loading
                    ? C.blue || "#0284c7"
                    : C.textLight || "#94a3b8",
                cursor: otp.length === 6 && !loading ? "pointer" : "default",
                fontFamily: font,
              }}
            >
              {loading ? "Verifying..." : "Verify & Complete"}
            </button>

            <div className="flex justify-center sm:justify-start sm:ml-2">
              {canResend ? (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loading}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-800 py-1 cursor-pointer"
                >
                  Resend OTP
                </button>
              ) : (
                <span className="text-xs font-mono text-slate-400 py-1">
                  Resend in {formatTimer(timer)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {error &&
        (isAuth4037 ? (
          <div className="mt-3 p-3.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-xs">
            <div className="flex items-start gap-2.5">
              <span className="text-base leading-none">
                <InlineIcon name="warning" size={16} />
              </span>
              <div className="flex-1 space-y-2">
                <p className="font-semibold text-amber-950">
                  GST Portal API Access Required (Code: AUTH4037)
                </p>
                <p className="text-amber-800 leading-relaxed text-[11px]">
                  Government GST regulations require the taxpayer to enable
                  third-party API access in their GST Portal profile before an
                  OTP can be dispatched.
                </p>
                <div className="bg-white/90 p-2.5 rounded border border-amber-200/70 space-y-1 text-[11px] text-slate-700">
                  <div className="font-medium text-slate-900 mb-1">
                    How to enable API Access on GST Portal (takes ~1 minute):
                  </div>
                  <ol className="list-decimal list-inside space-y-1 ml-0.5">
                    <li>
                      Log in to{" "}
                      <a
                        href="https://services.gst.gov.in/services/login"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline font-semibold"
                      >
                        services.gst.gov.in
                      </a>
                    </li>
                    <li>
                      Go to <strong>My Profile</strong> (top right) &gt;{" "}
                      <strong>Manage API Access</strong>
                    </li>
                    <li>
                      Set <strong>Enable API Access</strong> to{" "}
                      <strong>Yes</strong>, choose Duration (e.g. 30 days), and
                      click <strong>Confirm</strong>
                    </li>
                    <li>
                      Return here and click &ldquo;Send GST OTP&rdquo; again
                    </li>
                  </ol>
                </div>
                {onSwitchToPan && (
                  <div className="pt-1 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <span className="text-[11px] text-amber-800">
                      Cannot access GST Portal right now?
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        onSwitchToPan();
                      }}
                      className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-md transition shadow-xs cursor-pointer shrink-0"
                    >
                      Verify using PAN Card instead
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={errorBoxStyle}>{error}</div>
        ))}

      {/* Already registered organisation join request card */}
      {alreadyRegisteredData && (
        <OrganisationAlreadyRegisteredCard
          data={alreadyRegisteredData}
          defaultEmail={defaultEmail}
          onReset={() => {
            setAlreadyRegisteredData(null);
          }}
        />
      )}

      {/* Step 3: Verified Status Banner */}
      {verifiedData && (
        <div style={{ marginTop: 12 }}>
          <VerificationStatus
            status="VERIFIED"
            type="GST"
            referenceId={verifiedData.gstin || cleanGst}
            verifiedAt={new Date().toISOString()}
            details={{
              legalName: verifiedData.legalName,
              tradeName: verifiedData.tradeName,
              address: verifiedData.address,
              signatories: verifiedData.signatories,
            }}
          />
          <div
            style={{
              marginTop: 8,
              padding: "10px 12px",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 6,
              color: "#166534",
              fontSize: 12,
              fontWeight: 600,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>
              <InlineIcon name="check" size={12} /> GSTIN verified. Registered
              signatory authority confirmed.
            </span>
            <button
              type="button"
              onClick={handleReset}
              className="text-xs text-slate-500 hover:text-slate-800 font-semibold underline cursor-pointer ml-3"
            >
              Change GSTIN
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const boxStyle = {
  border: `1px solid ${C.divider || "#cbd5e1"}`,
  borderRadius: 8,
  padding: 14,
  margin: "10px 0 14px",
  background: "#ffffff",
  fontFamily: font,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const badgeStyle = {
  fontSize: 10,
  fontWeight: 700,
  background: "#E7F4EC",
  color: "#08833E",
  padding: "3px 8px",
  borderRadius: 4,
  border: "1px solid rgba(8, 131, 62, 0.2)",
};

const inputStyle = {
  flex: 1,
  padding: "8px 12px",
  fontSize: 13,
  letterSpacing: 1.5,
  border: `1px solid ${C.dividerStrong || "#cbd5e1"}`,
  borderRadius: 6,
  fontFamily: font,
};

const buttonStyle = {
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 600,
  color: "white",
  border: 0,
  borderRadius: 6,
  fontFamily: font,
};

const errorBoxStyle = {
  fontSize: 11,
  color: C.red || "#ef4444",
  background: C.redLight || "#fef2f2",
  padding: "6px 10px",
  borderRadius: 4,
  marginTop: 8,
};
